// js/custom-element/section/product/variants.js
if (!customElements.get('product-variants')) {
  var ProductVariants = class extends CustomHTMLElement {
    async connectedCallback() {
      this.masterSelector = document.getElementById(this.getAttribute('form-id')).id;
      this.optionSelectors = Array.from(this.querySelectorAll('[data-selector-type]'));
      if (!this.masterSelector) {
        console.warn(`The variant selector for product with handle ${this.productHandle} is not linked to any product form.`);
        return;
      }
      this.product = await ProductLoader.load(this.productHandle);
      this.delegate.on('change', '[name^="option"]', (event) => this._onOptionChanged(event));
      this.masterSelector.addEventListener('change', this._onMasterSelectorChanged.bind(this));
      this._updateDisableSelectors();
      this._updateOptionStatuses();
      this.selectVariant(this.selectedVariant['id']);

      // Emit initial validation state and update styles
      const hasUnselectedRequired = this._hasUnselectedRequiredOptions();
      triggerEvent(this.masterSelector.form, 'variant:validation-changed', {
        hasUnselectedRequiredOptions: hasUnselectedRequired
      });
      this._updateRequiredOptionStyles();
    }
    get selectedVariant() {
      return this._getVariantById(parseInt(this.masterSelector.value));
    }
    get productHandle() {
      return this.getAttribute('handle');
    }
    get hideSoldOutVariants() {
      return this.hasAttribute('hide-sold-out-variants');
    }
    get updateUrl() {
      return this.hasAttribute('update-url');
    }
    get requiredOptions() {
      const requiredOptionsAttr = this.getAttribute('data-required-options');
      if (!requiredOptionsAttr) return [];
      return requiredOptionsAttr.split(',').map(opt => opt.trim()).filter(opt => opt);
    }
    /**
     * Select a new variant by its ID
     */
    selectVariant(id, isSwatchChange = false) {
      if (!this._isVariantSelectable(this._getVariantById(id))) {
        id = this._getFirstMatchingAvailableOrSelectableVariant()['id'];
      }
      if (this.selectedVariant?.id === id) {
        return;
      }
      this.masterSelector.value = id;
      this.masterSelector.dispatchEvent(new Event('change', { bubbles: true }));
      if (this.updateUrl && history.replaceState) {
        const newUrl = new URL(window.location.href);
        if (id) {
          newUrl.searchParams.set('variant', id);
        } else {
          newUrl.searchParams.delete('variant');
        }
        window.history.replaceState({ path: newUrl.toString() }, '', newUrl.toString());
      }
      this._updateDisableSelectors();
      this._updateOptionStatuses();
      triggerEvent(this.masterSelector.form, 'variant:changed', { variant: this.selectedVariant, isSwatchChange });

      // Emit validation event for required options and update styles
      const hasUnselectedRequired = this._hasUnselectedRequiredOptions();
      triggerEvent(this.masterSelector.form, 'variant:validation-changed', {
        hasUnselectedRequiredOptions: hasUnselectedRequired
      });
      this._updateRequiredOptionStyles();
    }
    _onOptionChanged(event) {
      const selectorType = event?.target?.closest('[data-selector-type]')?.getAttribute('data-selector-type');
      const isSwatchChange = selectorType === 'swatch' || selectorType === 'variant-image';
      this.selectVariant(this._getVariantFromOptions()?.id, isSwatchChange);
    }
    _onMasterSelectorChanged() {
      const options = this.selectedVariant?.options || [];
      const requiredOptions = this.requiredOptions;
      const productOptions = this.product?.options || [];

      options.forEach((value, index) => {
        // Skip auto-selecting required options - they should remain unselected until user chooses
        if (productOptions[index]) {
          // Handle both object format {name: "Size", values: [...]} and string format "Size"
          const option = productOptions[index];
          const optionName = (typeof option === 'string' ? option : option.name).toLowerCase();
          if (requiredOptions.includes(optionName)) {
            // Check if user has already made a selection for this option
            const checkedInput = this.querySelector(`input[name="option${index + 1}"]:checked`);
            const selectInput = this.querySelector(`select[name="option${index + 1}"]`);

            // For radio/checkbox inputs - if none checked, skip
            if (!checkedInput && !selectInput) {
              return;
            }

            // For select dropdowns - check if display still shows "Select..."
            if (selectInput) {
              const displaySpan = this.querySelector(`[data-bind-value="${selectInput.getAttribute('data-bind-value')}"]`) ||
                                  selectInput.closest('.select-wrapper')?.querySelector('.select__selected-value');
              if (displaySpan && displaySpan.textContent.trim().toLowerCase().startsWith('select')) {
                return; // Skip - user hasn't selected yet
              }
            }

            // For radio inputs without selection
            if (!checkedInput && !selectInput) {
              return;
            }
          }
        }

        let input = this.querySelector(`input[name="option${index + 1}"][value="${CSS.escape(value)}"], select[name="option${index + 1}"]`),
          triggerChangeEvent = false;
        if (input.tagName === 'SELECT') {
          triggerChangeEvent = input.value !== value;
          input.value = value;
        } else if (input.tagName === 'INPUT') {
          triggerChangeEvent = !input.checked && input.value === value;
          input.checked = input.value === value;
        }
        if (triggerChangeEvent) {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
    /**
     * Get the product variant by its ID
     */
    _getVariantById(id) {
      return this.product['variants'].find((variant) => variant['id'] === id);
    }
    /**
     * Get the variant based on the options
     */
    _getVariantFromOptions() {
      const options = this._getSelectedOptionValues();
      return this.product['variants'].find((variant) => {
        return variant['options'].every((value, index) => value === options[index]);
      });
    }
    /**
     * Detect if a specific variant is selectable. This is used when the "hide sold out variant" option is enabled, to allow
     * to return true only if the variant is actually available
     */
    _isVariantSelectable(variant) {
      if (!variant) {
        return false;
      } else {
        return variant['available'] || (!this.hideSoldOutVariants && !variant['available']);
      }
    }
    /**
     * This method is used internally to select an available or selectable variant, when the current choice does not
     * match the requirements. For instance, if sold out variants are configured to be hidden, but that the choices end
     * up being a non-valid variant, the theme automatically changes the variant to match the requirements. In the case
     * the customer end up on variant combinations that do not exist, it also switches to a valid combination.
     *
     * The algorithm is as follows: if we have for instance three options "Color", "Size" and "Material", we pop the last
     * option (Material) and try to find the first available variant for the given Color and Size. If none is found we
     * remove the second option (Size) and try to find the first available variant for the selected color. Finally, if none
     * is found we return the first available variant independently of any choice.
     */
    _getFirstMatchingAvailableOrSelectableVariant() {
      let options = this._getSelectedOptionValues(),
        matchedVariant = null,
        slicedCount = 0;
      do {
        options.pop();
        slicedCount += 1;
        matchedVariant = this.product['variants'].find((variant) => {
          if (this.hideSoldOutVariants) {
            return variant['available'] && variant['options'].slice(0, variant['options'].length - slicedCount).every((value, index) => value === options[index]);
          } else {
            return variant['options'].slice(0, variant['options'].length - slicedCount).every((value, index) => value === options[index]);
          }
        });
      } while (!matchedVariant && options.length > 0);
      return matchedVariant;
    }
    _getSelectedOptionValues() {
      const options = [];
      Array.from(this.querySelectorAll('input[name^="option"]:checked, select[name^="option"]')).forEach((option) => options.push(option.value));
      return options;
    }
    /**
     * Check if all required options have been selected
     */
    _hasUnselectedRequiredOptions() {
      const requiredOptions = this.requiredOptions;
      if (requiredOptions.length === 0) return false;

      // Get all product options (names) from the product
      const productOptions = this.product?.options || [];

      // Check each required option to see if it has a selected value
      for (let i = 0; i < productOptions.length; i++) {
        // Handle both object format {name: "Size", values: [...]} and string format "Size"
        const option = productOptions[i];
        const optionName = (typeof option === 'string' ? option : option.name).toLowerCase();

        // Check if this option is in the required list
        if (requiredOptions.includes(optionName)) {
          const optionNumber = i + 1;
          const checkedInput = this.querySelector(`input[name="option${optionNumber}"]:checked`);
          const selectInput = this.querySelector(`select[name="option${optionNumber}"]`);

          // If it's a radio/checkbox, check if one is checked
          if (checkedInput) {
            continue; // This option is selected, check next
          }

          // If it's a select, check display text to see if user selected (not "Select...")
          if (selectInput) {
            const displaySpan = selectInput.closest('.select-wrapper')?.querySelector('.select__selected-value');
            if (displaySpan && !displaySpan.textContent.trim().toLowerCase().startsWith('select')) {
              continue; // User has made a selection
            }
          }

          // If we get here, this required option is not selected
          return true;
        }
      }

      return false;
    }
    /**
     * Update CSS classes on required option selectors to show visual feedback
     */
    _updateRequiredOptionStyles() {
      const requiredOptions = this.requiredOptions;
      if (requiredOptions.length === 0) return;

      const productOptions = this.product?.options || [];

      for (let i = 0; i < productOptions.length; i++) {
        // Handle both object format {name: "Size", values: [...]} and string format "Size"
        const option = productOptions[i];
        const optionName = (typeof option === 'string' ? option : option.name).toLowerCase();
        const optionSelector = this.optionSelectors[i];

        if (!optionSelector) continue;

        // Check if this option is in the required list
        if (requiredOptions.includes(optionName)) {
          const optionNumber = i + 1;
          const checkedInput = this.querySelector(`input[name="option${optionNumber}"]:checked`);
          const selectInput = this.querySelector(`select[name="option${optionNumber}"]`);

          // Determine if this required option is unselected
          let isUnselected = true;
          if (checkedInput) {
            isUnselected = false;
          } else if (selectInput) {
            // For selects, check display text
            const displaySpan = selectInput.closest('.select-wrapper')?.querySelector('.select__selected-value');
            if (displaySpan && !displaySpan.textContent.trim().toLowerCase().startsWith('select')) {
              isUnselected = false;
            }
          }

          // Toggle the required-unselected class
          optionSelector.classList.toggle('required-unselected', isUnselected);
        } else {
          // Remove class from non-required options
          optionSelector.classList.remove('required-unselected');
        }
      }
    }
    /**
     * Scroll to and highlight the first unselected required option
     */
    scrollToFirstUnselectedRequired() {
      // Ensure styles are updated before scrolling
      this._updateRequiredOptionStyles();

      const requiredOptions = this.requiredOptions;
      if (requiredOptions.length === 0) return;

      const productOptions = this.product?.options || [];

      for (let i = 0; i < productOptions.length; i++) {
        // Handle both object format {name: "Size", values: [...]} and string format "Size"
        const option = productOptions[i];
        const optionName = (typeof option === 'string' ? option : option.name).toLowerCase();

        if (requiredOptions.includes(optionName)) {
          const optionNumber = i + 1;
          const checkedInput = this.querySelector(`input[name="option${optionNumber}"]:checked`);
          const selectInput = this.querySelector(`select[name="option${optionNumber}"]`);

          // Check if this required option is unselected
          let isUnselected = true;
          if (checkedInput) {
            isUnselected = false;
          } else if (selectInput) {
            const displaySpan = selectInput.closest('.select-wrapper')?.querySelector('.select__selected-value');
            if (displaySpan && !displaySpan.textContent.trim().toLowerCase().startsWith('select')) {
              isUnselected = false;
            }
          }

          if (isUnselected) {
            const optionSelector = this.optionSelectors[i];
            if (optionSelector) {
              // Add required-unselected class for outline
              optionSelector.classList.add('required-unselected');

              // Scroll into view
              optionSelector.scrollIntoView({ behavior: 'smooth', block: 'center' });

              // Add shake animation
              optionSelector.classList.add('required-shake');
              setTimeout(() => {
                optionSelector.classList.remove('required-shake');
              }, 2000);
            }
            return; // Stop at first unselected
          }
        }
      }
    }
    /**
     * We add specific class to sold out variants based on the selectors
     */
    _updateDisableSelectors() {
      const selectedVariant = this.selectedVariant;
      if (!selectedVariant) {
        return;
      }
      this._updateDisableSelectorsForOptionLevel(0, selectedVariant);
    }
    _updateDisableSelectorsForOptionLevel(level, selectedVariant) {
      if (!this.optionSelectors[level]) {
        return;
      }
      const applyClassToSelector = (selector, valueIndex, available, hasAtLeastOneCombination) => {
        let selectorType = selector.getAttribute('data-selector-type'),
          cssSelector = '';
        switch (selectorType) {
          case 'swatch':
            cssSelector = `.color-swatch:nth-child(${valueIndex + 1})`;
            break;
          case 'variant-image':
            cssSelector = `.variant-swatch:nth-child(${valueIndex + 1})`;
            break;
          case 'block':
            cssSelector = `.block-swatch:nth-child(${valueIndex + 1})`;
            break;
          case 'dropdown':
            cssSelector = `.combo-box__option-item:nth-child(${valueIndex + 1})`;
            break;
        }
        selector.querySelector(cssSelector).toggleAttribute('hidden', !hasAtLeastOneCombination);
        if (this.hideSoldOutVariants) {
          selector.querySelector(cssSelector).toggleAttribute('hidden', !available);
        } else {
          selector.querySelector(cssSelector).classList.toggle('is-disabled', !available);
        }
      };
      const hasCombination = (variant, level2, value, selectedVariant2) => {
        return Array.from({ length: level2 + 1 }, (_, i) => {
          if (i === level2) {
            return variant[`option${level2 + 1}`] === value;
          } else {
            return variant[`option${i + 1}`] === selectedVariant2[`option${i + 1}`];
          }
        }).every((condition) => condition);
      };
      this.product['options'][level]['values'].forEach((value, valueIndex) => {
        const hasAtLeastOneCombination = this.product['variants'].some((variant) => hasCombination(variant, level, value, selectedVariant) && variant);
        const hasAvailableVariant = this.product['variants'].some((variant) => hasCombination(variant, level, value, selectedVariant) && variant['available']);
        applyClassToSelector(this.optionSelectors[level], valueIndex, hasAvailableVariant, hasAtLeastOneCombination);
        this._updateDisableSelectorsForOptionLevel(level + 1, selectedVariant);
      });
    }
    /**
     * Update option status badges (e.g., "Ready to Ship", "Pre-Order") dynamically
     */
    _updateOptionStatuses() {
      // Check if variantOptionData is available
      if (typeof variantOptionData === 'undefined' || !variantOptionData) {
        return;
      }

      const selectedVariant = this.selectedVariant;
      if (!selectedVariant) {
        return;
      }

      // Get currently selected option values
      const selectedOptions = this._getSelectedOptionValues();

      // Loop through each option position
      this.optionSelectors.forEach((selector, optionIndex) => {
        const selectorType = selector.getAttribute('data-selector-type');

        // Only update dropdown selectors
        if (selectorType !== 'dropdown') {
          return;
        }

        const optionName = variantOptionData.options[optionIndex];
        if (!optionName) {
          return;
        }

        // Skip color/colour options
        const optionNameLower = optionName.toLowerCase();
        if (optionNameLower.includes('color') || optionNameLower.includes('colour')) {
          return;
        }

        // Get all option buttons in this selector
        const optionButtons = selector.querySelectorAll('.combo-box__option-item');

        optionButtons.forEach((button, valueIndex) => {
          const optionValue = button.getAttribute('value');

          // Find all variants that match:
          // 1. This option value
          // 2. All previously selected options (before this option position)
          // 3. Are available
          const matchingVariants = Object.values(variantOptionData.variants).filter(variant => {
            // Check if this variant has the current option value
            if (variant[`option${optionIndex + 1}`] !== optionValue) {
              return false;
            }

            // Check if this variant is available
            if (!variant.available) {
              return false;
            }

            // Check if this variant matches all previously selected options
            for (let i = 0; i < optionIndex; i++) {
              if (variant[`option${i + 1}`] !== selectedOptions[i]) {
                return false;
              }
            }

            return true;
          });

          // Collect unique statuses from matching variants
          const statuses = [...new Set(
            matchingVariants
              .map(v => v.status)
              .filter(status => status && status !== '' && status !== 'In Stock')
          )];

          // Determine which status to show
          let displayStatus = '';
          if (statuses.length > 0) {
            // Prioritize "Ready to Ship"
            if (statuses.includes('Ready to Ship')) {
              displayStatus = 'Ready to Ship';
            } else {
              // Use the first unique status
              displayStatus = statuses[0] ?? 'Sold Out';
            }
          } else {
            displayStatus = 'Sold Out';
          }

          // Update the status badge
          const statusBadge = button.querySelector('.combo-box__option-status');
          if (statusBadge) {
            if (displayStatus) {
              statusBadge.textContent = displayStatus;
              statusBadge.style.display = '';
            } else {
              statusBadge.textContent = '';
              statusBadge.style.display = 'none';
            }
          }
        });
      });
    }
  };
  window.customElements.define('product-variants', ProductVariants);
}
